import { cn, formatDateValue, toNumber } from '@/lib/utils';

import {
  avance,
  DESEMPENO_BARRAS,
  diasRestantes,
  diasRestantesDe,
  valorConUnidad,
  type MetaTone,
} from '../lib/metas-format';
import type { Goal } from '../metas.types';

interface MetaProgressProps {
  goal: Goal;
  /** Muestra el avance, las fechas y los días restantes. */
  detallado?: boolean;
  className?: string;
}

const TONO_TEXTO: Record<MetaTone, string> = {
  brand: 'text-brand-600',
  emerald: 'text-emerald-600',
  amber: 'text-amber-600',
  rose: 'text-rose-600',
  ink: 'text-ink-500',
};

/** Barra de avance de una meta, con sus valores. */
export function MetaProgress({ goal, detallado = false, className }: MetaProgressProps) {
  const porcentaje = avance(goal);
  const desempeno = goal.performance_status ?? 'on_track';
  const color = DESEMPENO_BARRAS[desempeno] ?? 'bg-brand-500';

  return (
    <div className={cn('space-y-2', className)}>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-ink-100">
        <div
          role="progressbar"
          aria-valuenow={Math.round(porcentaje)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Avance de ${goal.goal_name}`}
          style={{ width: `${porcentaje}%` }}
          className={cn('h-full rounded-full transition-all duration-500', color)}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
        <span className="font-mono text-ink-600 tabular-nums">
          {valorConUnidad(goal.current_value, goal.unit_measurement)}
          <span className="text-ink-300"> / </span>
          {valorConUnidad(goal.target_value, goal.unit_measurement)}
        </span>

        <span className={cn('font-semibold tabular-nums', TONO_TEXTO[desempeno as MetaTone] ?? 'text-ink-500')}>
          {porcentaje.toFixed(1)}%
        </span>
      </div>

      {detallado ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-400">
          <span>
            {formatDateValue(goal.start_date)} → {formatDateValue(goal.end_date)}
          </span>
          <span>{diasRestantes(diasRestantesDe(goal))}</span>
          {toNumber(goal.target_value) > 0 ? (
            <span>
              Falta{' '}
              {valorConUnidad(
                Math.max(toNumber(goal.target_value) - toNumber(goal.current_value), 0),
                goal.unit_measurement,
              )}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
